import { useEffect, useRef, useState } from 'react';
import { Check, Edit2, Loader2, MoreVertical, Plus, Save, SaveAll } from 'lucide-react';
import { useSessionStore } from '@/stores';
import { sessionApi } from '@/services/api';
import { useCompactMode } from '@/hooks/useCompactMode';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface SessionControlsProps {
  onNewSession?: () => void;
  onSaveSession?: () => void;
  onSaveAsSession?: () => void;
  showSaveButton?: boolean;
  showSaveAsButton?: boolean;
}

export function SessionControls({
  onNewSession,
  onSaveSession,
  onSaveAsSession,
  showSaveButton,
  showSaveAsButton,
}: SessionControlsProps) {
  const { compactMode } = useCompactMode();
  const {
    sessionId,
    sessionName,
    sessionHasBeenSaved,
    isAutoSaving,
    hasUnsavedChanges,
    saveError,
    lastSavedAt,
    setSessionName,
    markSessionDirty,
  } = useSessionStore();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sessionName);
  const [nameError, setNameError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const renamePending = useRef(false);
  const isNamedSession = sessionHasBeenSaved && !!sessionId && !sessionId.startsWith('unsaved-');

  useEffect(() => {
    setName(sessionName);
    setEditing(false);
    setNameError('');
  }, [sessionName, sessionId]);

  const saveName = async () => {
    if (renamePending.current) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Enter a session name.');
      return;
    }
    if (trimmed === sessionName) {
      setEditing(false);
      return;
    }
    renamePending.current = true;
    setRenaming(true);
    setNameError('');
    try {
      if (isNamedSession) await sessionApi.renameSession(sessionId, trimmed);
      if (useSessionStore.getState().sessionId !== sessionId) return;
      setSessionName(trimmed);
      if (!isNamedSession) markSessionDirty();
      setEditing(false);
      window.dispatchEvent(new Event('sessions-updated'));
    } catch {
      setNameError('Could not rename. Try again.');
    } finally {
      renamePending.current = false;
      setRenaming(false);
    }
  };

  const status = isAutoSaving
    ? 'Saving…'
    : saveError
      ? 'Couldn’t save'
      : hasUnsavedChanges
        ? 'Unsaved changes'
        : lastSavedAt && !isNamedSession
          ? 'Backup saved'
          : isNamedSession
            ? 'Saved'
            : 'Not saved yet';

  return (
    // Two rows, not three. The save status used to own a row of its own between the name
    // and the buttons, which cost a third of the block's height to carry one short
    // phrase — it sits on the name's row now, where there is room for it and where it is
    // read in the same glance as the name it describes.
    <div className="min-w-0 space-y-1.5">
      {editing ? (
        <div className="flex min-w-0 items-center gap-1">
          <Input
            value={name}
            onChange={event => setName(event.target.value)}
            onBlur={saveName}
            onKeyDown={event => {
              if (event.key === 'Enter') event.currentTarget.blur();
              if (event.key === 'Escape') {
                setName(sessionName);
                setNameError('');
                setEditing(false);
              }
            }}
            aria-label="Session name"
            aria-invalid={!!nameError}
            autoFocus
            disabled={renaming}
            className="h-7 min-w-0 flex-1 text-sm"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="Confirm session name"
            disabled={renaming}
            onMouseDown={event => event.preventDefault()}
            onClick={saveName}
          >
            {renaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
        </div>
      ) : (
        <div className="flex min-w-0 items-center gap-1.5">
          <button
            onClick={() => setEditing(true)}
            disabled={isAutoSaving}
            className="flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded px-1 text-left text-sm font-medium text-slate-900 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
            title={sessionName ? `${sessionName} — click to rename` : 'Rename session'}
          >
            <span className="min-w-0 flex-1 truncate">{sessionName || 'Untitled session'}</span>
            <Edit2 className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          </button>

          {/* Beside the name rather than under it. `shrink-0` so the status stays whole
              and the name — which has a tooltip and a rename dialog behind it — is the
              one that truncates. */}
          <div
            className="flex shrink-0 items-center gap-1 text-xs"
            role="status"
            aria-live="polite"
          >
            {isAutoSaving && <Loader2 className="h-3 w-3 animate-spin" />}
            <span
              className={
                saveError ? 'text-red-700' : hasUnsavedChanges ? 'text-amber-700' : 'text-slate-500'
              }
              title={
                saveError ||
                (lastSavedAt ? `Last saved ${new Date(lastSavedAt).toLocaleString()}` : undefined)
              }
            >
              {status}
            </span>
            {saveError && (
              <button onClick={onSaveSession} className="font-medium text-blue-700 underline">
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      {nameError && (
        <p role="alert" className="px-1 text-xs text-red-700">
          {nameError}
        </p>
      )}
      <div className="flex min-w-0 items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={onNewSession}
          disabled={isAutoSaving || renaming}
          className="h-7 min-w-0 flex-1 gap-1 px-2 text-xs"
          aria-label="New session"
          title="New session"
        >
          <Plus className="h-3.5 w-3.5" />
          {compactMode ? 'New' : 'New session'}
        </Button>
        {showSaveButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSaveSession}
            disabled={isAutoSaving || renaming}
            className="h-7 shrink-0 gap-1 px-2 text-xs"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        )}
        {showSaveAsButton && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                disabled={isAutoSaving || renaming}
                aria-label="More session actions"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={onSaveAsSession}>
                <SaveAll className="mr-2 h-4 w-4" />
                Save a copy…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
